import { ref } from 'vue'

import {
  firstWizardStepIndex,
  isWizardStepIndex,
  wizardSteps,
} from '../../constants/current-student-advanced-standing-form/wizard'

export type WizardStepClass = {
  current: boolean
  done: boolean
  disabled: boolean
}

export type UsePublicFormWizardOptions = {
  onStepChanged?: (index: number) => void
}

export function wizardStepClass(index: number, currentStepIndex: number): WizardStepClass {
  return {
    current: index === currentStepIndex,
    done: index < currentStepIndex,
    disabled: index > currentStepIndex,
  }
}

export function wizardStepAriaDisabled(index: number, currentStepIndex: number): 'true' | 'false' {
  return index > currentStepIndex ? 'true' : 'false'
}

export function wizardStepAriaSelected(index: number, currentStepIndex: number): 'true' | 'false' {
  return index === currentStepIndex ? 'true' : 'false'
}

export function canNavigateToWizardStep(
  index: number,
  currentStepIndex: number,
  applicationComplete: boolean,
): boolean {
  return !applicationComplete && isWizardStepIndex(index) && index < currentStepIndex
}

export function usePublicFormWizard(options: UsePublicFormWizardOptions = {}) {
  const currentStepIndex = ref(firstWizardStepIndex)

  function navigateToStep(index: number): boolean {
    if (!isWizardStepIndex(index)) return false
    if (currentStepIndex.value === index) return false

    currentStepIndex.value = index
    options.onStepChanged?.(index)
    return true
  }

  function navigateToPreviousStep(): boolean {
    if (currentStepIndex.value <= firstWizardStepIndex) return false

    return navigateToStep(currentStepIndex.value - 1)
  }

  function canNavigateToStep(index: number, applicationComplete: boolean): boolean {
    return canNavigateToWizardStep(index, currentStepIndex.value, applicationComplete)
  }

  function stepClass(index: number): WizardStepClass {
    return wizardStepClass(index, currentStepIndex.value)
  }

  function stepAriaDisabled(index: number): 'true' | 'false' {
    return wizardStepAriaDisabled(index, currentStepIndex.value)
  }

  function stepAriaSelected(index: number): 'true' | 'false' {
    return wizardStepAriaSelected(index, currentStepIndex.value)
  }

  return {
    wizardSteps,
    currentStepIndex,
    navigateToStep,
    navigateToPreviousStep,
    canNavigateToStep,
    stepClass,
    stepAriaDisabled,
    stepAriaSelected,
  }
}
